import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Box,
    Card,
    Divider,
    Grid,
    Slider,
    Checkbox,
    TextField,
    Typography,
    useTheme,
    Paper,
    Tooltip as MuiTooltip,
    CircularProgress
} from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import {
    Area, Bar, CartesianGrid, ComposedChart, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { PerformanceChart } from './components';
import {
    computeDomainsFromCumulativeReturns,
    ClusterReturnSeries,
} from './calculation'; // your helper file

interface ChartDataParams extends ClusterReturnSeries {
    clusteringAssignment: string;
    clusterIds: number[];
    cumulativeReturns: Record<number, number>[];
    combinedReturns: number[];
}

type ChartPoint = {
    timestamp: string;
    price: number;
    clusterReturns: { [clusterId: number]: number };
    activeClusterId?: number;
    [key: string]: any; // This allows dynamic keys like cluster_area_${id}
};

type ChartDataResult = {
    chartData: ChartPoint[];
    priceDomain: [number, number];
    returnDomain: [number, number];
    logPriceDomain: [number, number];
    logReturnDomain: [number, number];
    cumulativeReturns: Record<number, number>[]; // <-- added
};

export const useChartData = ({
    clusterIds,
    clusteringAssignment,
    timeseriesData,
    firstPrice,
    cumulativeReturns,
    combinedReturns,
}: ChartDataParams): ChartDataResult => {
    return useMemo(() => {
        const prices = timeseriesData.map(d => d.price);
        const { priceDomain, returnDomain, logPriceDomain, logReturnDomain } = computeDomainsFromCumulativeReturns(
            prices,
            cumulativeReturns,
            combinedReturns,
            firstPrice
        );

        const chartData = buildChartPoints({
            timeseriesData,
            cumulativeReturns,
            clusterIds,
            returnDomain
        }).map((point, i) => ({
            ...point,
            combinedReturn: combinedReturns[i]
        }));

        return {
            chartData,
            priceDomain,
            returnDomain,
            logPriceDomain,
            logReturnDomain,
            cumulativeReturns,
        };
    }, [clusterIds, clusteringAssignment, timeseriesData, firstPrice, cumulativeReturns, combinedReturns]);
};

interface BuildChartPointsParams {
    timeseriesData: ClusterReturnSeries['timeseriesData'];
    cumulativeReturns: Record<number, number>[];
    clusterIds: number[];
    returnDomain: [number, number];
}

function buildChartPoints({
    timeseriesData,
    cumulativeReturns,
    clusterIds,
    returnDomain,
}: BuildChartPointsParams): ChartPoint[] {
    return timeseriesData.map((entry, i) => {
        const clusterReturns = cumulativeReturns[i];
        const point: any = {
            timestamp: entry.timestamp,
            price: entry.price,
            activeClusterId: entry.activeClusterId,
            clusterReturns,
        };

        for (const id of clusterIds) {
            point[`cluster_bg_${id}`] = entry.activeClusterId === id ? 1 : 0;
            point[`cluster_area_${id}`] = entry.activeClusterId === id
                ? returnDomain[1]
                : returnDomain[0];
        }

        return point;
    });
}

type Props = {
    clustering: {
        id: string;
        name: string;
        series: string;
    };
    clusteringAssignment: string;
    clusterIds: number[];
    cumulativeReturns: any; // type properly later
    enabledClusters: Record<number, boolean>;
    combinedReturns: any;
    clusterReturnSeries: ClusterReturnSeries;
}

const ClusteringPerformanceChart = ({ clustering, clusteringAssignment, clusterIds, cumulativeReturns, enabledClusters, combinedReturns, clusterReturnSeries }: Props) => {
    const theme = useTheme();

    const chartDataResult = useChartData({
        clusteringAssignment,
        clusterIds,
        cumulativeReturns,
        combinedReturns,
        ...clusterReturnSeries,
    });

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                Performance – {clustering.name}
            </Typography>
            <PerformanceChart
                clusterIds={clusterIds}
                enabledClusters={enabledClusters}
                showCombinedStrategy={combinedReturns.at(-1) !== 1}
                data={chartDataResult}
            />
        </Box>
    );
};

export default ClusteringPerformanceChart;
